import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, Grid, Paper, Typography } from '@mui/material'
import { currencyFormatter } from 'src/Libs/shop/utils/formatter'
import React from 'react'
import { makeStyles } from 'tss-react/mui'
import { ShopCheckoutSideBarResponse } from 'src/Libs/shop/models'

interface ConfirmCheckoutSummaryProps {
  product: ShopCheckoutSideBarResponse | null
  blockOrder: boolean
  handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void
}

const useStyles = makeStyles()(theme => ({
  normalText: {
    color: theme.palette.text.primary,
    borderColor: theme.palette.grey.grey03,
  },
  spaceBetweenCenter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  totalContainer: {
    backgroundColor: theme.palette.shades.shade04,
    padding: theme.spacing(4),
  },
  button: {
    marginTop: '24px',
  },
}))

const ConfirmCheckoutSummary: React.FC<ConfirmCheckoutSummaryProps> = ({ product, blockOrder, handleSubmit }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()

  const totalPriceWithoutVat = (product && currencyFormatter(product?.price.netPrice)) ?? 0
  const calculatedShippingPrice = (product?.deliveries || []).reduce((total, delivery) => total + (delivery.shippingCosts.totalPrice || 0), 0)
  const shippingPrice = currencyFormatter(calculatedShippingPrice)
  const total = (product && currencyFormatter(product?.price.positionPrice)) ?? 0
  const savingsPrice = product?.extensions?.savings.total ?? 0
  const formattedSavingsPrice = currencyFormatter(Math.abs(savingsPrice))
  const priceDiscount = savingsPrice < 0

  return (
    <Grid container spacing={3} alignItems="center" justifyContent="center">
      <Grid item xs={12} md={6} />
      <Grid item xs={12} md={6} sx={{ paddingLeft: '24px', paddingRight: '24px' }}>
        <Paper className={classes.totalContainer}>
          <Typography variant="h2" sx={{ fontWeight: 400, marginBottom: '12px' }} className={classes.normalText}>
            {t('shop.confirm-checkout.summary')}
          </Typography>
          <Box className={classes.spaceBetweenCenter}>
            <Typography>{t('shop.confirm-checkout.total')}</Typography>
            <Typography>{total}</Typography>
          </Box>

          <Box className={classes.spaceBetweenCenter}>
            <Typography>{t('shop.confirm-checkout.shipping')}</Typography>
            <Typography>{shippingPrice}</Typography>
          </Box>
          {priceDiscount && (
            <Box className={classes.spaceBetweenCenter}>
              <Typography color="primary">{t('shop.confirm-checkout.price-saved')}</Typography>
              <Typography color="primary">{formattedSavingsPrice}</Typography>
            </Box>
          )}
          <Box className={classes.spaceBetweenCenter}>
            <Typography>{t('shop.confirm-checkout.total-price-without-vat')}</Typography>
            <Typography>{totalPriceWithoutVat}</Typography>
          </Box>
          {/**TODO: Commented out code, missing data from backend, will be used later. */
          /*
          {calculatedTaxes?.map((tax, index) => (
            <Box key={index} className={classes.spaceBetweenCenter}>
              <Typography>{`${t('shop.confirm-checkout.plus')} ${tax.taxRate} % ${t('shop.confirm-checkout.vat')}`}</Typography>
              <Typography>{currencyFormatter(tax.tax)}</Typography>
            </Box>
          ))}
          <Box className={classes.spaceBetweenCenter}>
            <Typography sx={{ fontWeight: 600 }}>{t('shop.confirm-checkout.total-price')}</Typography>
            <Typography sx={{ fontWeight: 600 }}>{totalPrice}</Typography>
          </Box> */}
        </Paper>
        <Button
          variant="contained"
          fullWidth
          className={classes.button}
          disabled={blockOrder}
          onClick={() => {
            handleSubmit()
          }}
        >
          {t('shop.confirm-checkout.pay-agreement')}
        </Button>
      </Grid>
      <Typography variant="h5" className={classes.normalText} color="inherit" sx={{ textAlign: 'center', marginTop: '80px' }}>
        * {t('shop.confirm-checkout.price-summary')}
      </Typography>
    </Grid>
  )
}

export default ConfirmCheckoutSummary
